/**
 * Live ↔ Dev backend switch — the successor to the legacy panel's env switcher.
 *
 * Only the DATA backend (wpcursor-backend) is tiered: main :3000 vs dev :3010.
 * When 'dev', every `/api/...` call is rewritten to `/api-dev/...`, which the
 * proxy (and nginx in prod) routes to the dev tier.
 *
 * Identity (wpcursor-admin-api) is NOT tiered — one service, one token. Because
 * that token verifies against either backend tier with the same public key,
 * switching environments needs NO re-login. That's the improvement over the old
 * panel, which kept a separate session per tier and forced a fresh sign-in.
 */

const KEY = 'wpcAdminEnv';
export type Env = 'live' | 'dev';

export function getEnv(): Env {
  const stored = localStorage.getItem(KEY);
  if (stored === 'live') return 'live';
  if (stored === 'dev') return 'dev';
  return defaultEnv();
}

/**
 * Default tier by host — until the tiers are ever split onto separate portals,
 * both stay switchable on either portal via the Live/Dev pills; only the landing
 * default differs. The prod portal (portal.wpos.ai) lands on Live; the dev portal
 * (portal.dev.wpos.ai) and localhost land on Dev. The prod backend now accepts
 * portal tokens, so Live-by-default on prod is safe.
 */
function defaultEnv(): Env {
  const host = typeof location !== 'undefined' ? location.hostname : '';
  return host === 'portal.wpos.ai' ? 'live' : 'dev';
}

export function setEnv(env: Env): void {
  localStorage.setItem(KEY, env === 'live' ? 'live' : 'dev');
}

/** Rewrites a data-backend path for the active tier. Portal paths are untouched. */
export function applyEnv(path: string): string {
  if (getEnv() === 'dev' && path.startsWith('/api/')) {
    return '/api-dev/' + path.slice('/api/'.length);
  }
  return path;
}
